import { useEffect, useState } from "react";
import type { Settings } from "@vereinfach/types";
import { errorMessage, mutate } from "../../lib/api";
import { useSettings as useSettingsContext } from "../../lib/settings";
import { useCurrentTenant } from "../../lib/currentTenant";
import { Field, SaveBar, Section } from "./common";

export function SettingsTemplate() {
  const { settings, refetch: refetchSettings } = useSettingsContext();
  const tenant = useCurrentTenant();

  const [local, setLocal] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) setLocal(settings);
  }, [settings]);

  function set<K extends keyof Settings>(k: K, v: Settings[K]) {
    setLocal((s) => (s ? { ...s, [k]: v } : s));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!local) return;
    setSaving(true);
    setError(null);
    try {
      await mutate<Settings>(
        `/tenants/${tenant.id}/settings`,
        {
          header_text: local.header_text,
          footer_text: local.footer_text,
        },
        "PATCH",
      );
      await refetchSettings();
      setSavedAt(Date.now());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (!local) return <p className="text-slate-400">Lädt…</p>;

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      <Section title="Rechnungs-Template">
        <p className="text-sm text-slate-500">
          Diese Texte erscheinen auf jeder Rechnung. Bei Entwürfen kannst du sie pro Rechnung
          überschreiben.
        </p>
        <Field label="Text oberhalb der Rechnungspositionen">
          <textarea
            rows={4}
            value={local.header_text ?? ""}
            onChange={(e) => set("header_text", e.target.value || null)}
            placeholder="z. B. Besten Dank für deine Mitgliedschaft."
            className="input"
          />
        </Field>
        <Field label="Text unterhalb / Fusszeile">
          <textarea
            rows={4}
            value={local.footer_text ?? ""}
            onChange={(e) => set("footer_text", e.target.value || null)}
            placeholder="z. B. Bitte überweise den Betrag bis zum angegebenen Datum."
            className="input"
          />
        </Field>
      </Section>

      <SaveBar saving={saving} savedAt={savedAt} error={error} />
    </form>
  );
}
